<template>
  <div
    class="nav-search"
    @click.stop="handleHrefClick"
  >
    <div class="nav-search-wrapper">
      <input
        ref="searchInput"
        class="nav-search-input"
        :placeholder="placeholder"
        @focus="isFocus = true"
        @keydown.enter="search('enter')"
        v-model="keyword"
        @click="inputClick"
      />
      <span
        v-show="keyword"
        class="suffix-icon suffix-icon-delete"
        @click="resetKeyword"
      >
        <i class="icon-clear"></i>
      </span>
      <button class="nav-search-btn" type="button" @click="search('search')">Search</button>
    </div>
    <history
      class="nav-search-history"
      v-show="isFocus && !keyword"
      :historyList="historyList"
      @del-history="delHistory"
      @clear-history="clearHistory"
    ></history>
    <associate
      class="nav-search-associate"
      v-show="isFocus && keyword && associateList.length > 0"
      :keyword="keyword"
      :associateList="associateList"
    ></associate>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import Associate from './associate';
import History from './history';
import { getSearchUrl, debounce } from '@/utils';
import storage from '@/utils/storage';
import { SEARCH_HISTORY_KEY } from '@/common/constant';
const { mapState } = createNamespacedHelpers('common');

export default {
  name: 'Search',
  components: {
    Associate,
    History,
  },
  data() {
    return {
      keyword: this.$store.state.common.keyword || '',
      isFocus: false,
      historyList: [],
      associateList: [],
    };
  },
  computed: {
    ...mapState([
      'app',
      'source'
    ]),
    placeholder () {
      return 'Enter your question';
    },
  },
  watch: {
    isFocus (val) {
      if (!val) {
        this.associateList = [];
      }
    },
    keyword () {
      this.debounceGetAssociateList && this.debounceGetAssociateList();
    },
    '$store.state.common.keyword': {
      immediate: true,
      handler (val) {
        this.keyword = val;
      },
    },
    '$route': {
      handler(val) {
        if(val.name !== 'search') {
          setTimeout(() => {
            this.keyword = '';
          }, 0);
        }
        this.globalClickListener();
      },
      immediate: true,
    }
  },
  async mounted () {
    this.historyList = storage.get(SEARCH_HISTORY_KEY) || [];
    // 监听全局click
    document.addEventListener('click', this.globalClickListener);
    // 设置防抖拉取联想词
    this.debounceGetAssociateList = debounce(this.getAssociateList, 300);
  },
  methods: {
    // 点击其他的地方才算模糊
    globalClickListener () {
      this.isFocus = false;
    },
    // 监听a标签跳转搜索，记录历史
    handleHrefClick (e) {
      const target = e.target;
      const keyword = target.getAttribute('data-keyword');
      if (target.tagName.toLocaleLowerCase() === 'a' && keyword) {
        this.addHistory(keyword);
      }
    },
    // 添加单个记录
    addHistory (word) {
      const list = [...this.historyList];
      const idx = list.findIndex((item) => item === word);

      // 如已有该搜索词，则置顶搜索词
      if (idx !== -1) {
        list.splice(idx, 1);
      }

      this.historyList = [word, ...list].slice(0, 5);
      storage.set(SEARCH_HISTORY_KEY, this.historyList);
    },
    // 删除单个记录
    delHistory (word) {
      const list = [...this.historyList];
      const idx = list.findIndex((item) => item === word);
      list.splice(idx, 1);
      this.historyList = list;
      storage.set(SEARCH_HISTORY_KEY, this.historyList);
    },
    /**
     * 执行搜索
     * @param {String} content 点击来源
     */
    search (content) {
      const { keyword } = this;
      const word = keyword;
      if (!word) return;
      
      this.addHistory(word);
      this.isFocus = false;
      const url = getSearchUrl(word);
      this.$router.push(url);
    },
    // 清除所有记录
    clearHistory () {
      this.historyList = [];
      storage.set(SEARCH_HISTORY_KEY, this.historyList);
    },
    // 获取联想词
    async getAssociateList () {
      if (!this.keyword) {
        this.associateList = [];
        return;
      }
      const data = await this.$service.search.getSearchAssociate({ keyword: this.keyword });
      if (!data) {
        this.associateList = [];
        return;
      }
      this.associateList = data;
    },
    /**
     * @method 清空搜索词
     */
    resetKeyword () {
      this.keyword = '';
      this.autoFocus();
    },
    /**
     * @method 输入框获取焦点
     */
    autoFocus () {
      this.$refs.searchInput && this.$refs.searchInput.focus();
    },
    routerChange(item) {
      this.addHistory(item);
      this.isFocus = false;
    },
    inputClick() {
      const { app, source } = this;
      this.$report('helpcenter_page', {
        action: 'click',
        comp: app,
        item: 'searchenter',
        source,
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.nav-search {
  width: 100%;
  position: relative;
  padding: 13px 0 12px;
  z-index: 9;

  &-wrapper {
    width: 100%;
    display: flex;
    font-size: 12px;
    position: relative;
  }

  &-input {
    flex: 1;
    border-radius: none;
    overflow: hidden;
    border: 1px solid var(--current-search-border-color);
    border-right: none;
    border-radius: 2px 0 0 2px;
    height: 30px;
    padding: 0 26px 0 10px;
    color: var(--current-search-color);
    line-height: 30px;
    box-sizing: border-box;
    background-color: var(--current-search-bg-color);

    &:hover {
      background-color: var(--current-bg-hover-color);
    }
  }

  .suffix-icon {
    position: absolute;
    top: 7px;
    text-align: center;
    cursor: pointer;
    right: 65px;

    & > i {
      font-size: 16px;
      color: var(--current-search-icon-color);
    }
  }

  &-btn {
    width: 57px;
    height: 30px;
    background: var(--current-search-primary-color);
    color: #fff;
    border-radius: 0 2px 2px 0;
    line-height: 30px;
    text-align: center;
    border: none;
    cursor: pointer;
    // user-select: none;
    font-weight: 500;

    &:hover {
      opacity: 0.9;
    }
  }

  &-history,
  &-associate {
    width: 100%;
    position: absolute;
    top: 51px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/page-navbar/search/index.vue